// Shared sheet for Create folder, Rename folder, and Rename file.
// The "mode" prop tunes the heading, primary-button label, and the
// initial text-selection behaviour (file renames select the stem so
// the extension is preserved without ceremony).

import { useEffect, useMemo, useRef, useState } from "react";
import { BottomSheet } from "../ui/bottom-sheet";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { validateName } from "../../lib/folderNames";

export type NewFolderMode = "create" | "rename-dir" | "rename-file";

interface Props {
  mode: NewFolderMode;
  destPath: string;
  siblings: string[];
  initialName: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onSubmit: (name: string) => Promise<void> | void;
}

const HEADING: Record<NewFolderMode, string> = {
  create: "New folder",
  "rename-dir": "Rename folder",
  "rename-file": "Rename file",
};

const CTA: Record<NewFolderMode, string> = {
  create: "Create",
  "rename-dir": "Rename",
  "rename-file": "Rename",
};

export function NewFolderSheet({
  mode,
  destPath,
  siblings,
  initialName,
  open,
  onOpenChange,
  onSubmit,
}: Props) {
  const [value, setValue] = useState(initialName);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const didApplyInitialSelection = useRef(false);

  const applySelection = (el: HTMLInputElement) => {
    if (mode === "rename-file") {
      const lastDot = initialName.lastIndexOf(".");
      const end = lastDot > 0 ? lastDot : initialName.length;
      el.setSelectionRange(0, end);
    } else {
      el.setSelectionRange(0, initialName.length);
    }
  };

  // Ref callback: the base-ui Dialog renders the popup (and therefore
  // our input) inside a portal, mounted AFTER our useEffect runs. So
  // by the time a plain effect fires, `inputRef.current` is still null
  // and setSelectionRange silently no-ops. Applying selection in the
  // ref callback runs exactly when the input mounts, which is also
  // before the Dialog's own focus-management effect fires.
  //
  // We guard with `didApplyInitialSelection` so focus/selection only
  // fires once per sheet open — without this, every re-render creates
  // a new callback identity, React re-invokes it, and setSelectionRange
  // snaps the cursor back to position 0 on every keystroke.
  const setInputRef = (el: HTMLInputElement | null) => {
    inputRef.current = el;
    if (el && open && !didApplyInitialSelection.current) {
      didApplyInitialSelection.current = true;
      el.focus();
      applySelection(el);
    }
  };

  const effectiveSiblings = useMemo(
    () => siblings.filter((s) => s !== initialName),
    [siblings, initialName],
  );
  const error = useMemo(
    () => validateName(value, effectiveSiblings),
    [value, effectiveSiblings],
  );

  // Reset the controlled value whenever the sheet re-opens with a
  // different initialName. Selection + focus are handled in the ref
  // callback (`setInputRef`) so we don't race the portal mount.
  useEffect(() => {
    if (open) {
      setValue(initialName);
      didApplyInitialSelection.current = false;
    }
  }, [open, initialName]);

  const canSubmit = error === null && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(value.trim());
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={HEADING[mode]}
      description={
        mode === "create" ? (
          <>Creating in <span className="font-mono text-text">{destPath || "/"}</span></>
        ) : (
          <>Renaming <span className="font-mono text-text">{initialName}</span></>
        )
      }
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {CTA[mode]}
          </Button>
        </>
      }
    >
      <input
        ref={setInputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void handleSubmit();
        }}
        aria-invalid={error !== null}
        data-testid="new-folder-input"
        className={cn(
          "h-11 w-full min-w-0 rounded-lg border border-border bg-transparent px-3 py-2 text-base outline-none md:h-8 md:px-2.5 md:py-1 md:text-sm",
          "focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/40",
          "aria-invalid:border-danger aria-invalid:ring-2 aria-invalid:ring-danger/30",
        )}
      />
      {error && value.length > 0 && (
        <p className="mt-2 text-xs text-danger">{error}</p>
      )}
    </BottomSheet>
  );
}
